import { map, findIndex, get, size } from "lodash"
import { useEffect } from "react"
import PropTypes from "prop-types"
import { CardContent, Paper } from "@mui/material"
import "./style.scss"
import { DATE } from "helpers/shapes"
import { computeCSVData } from "helpers/csv"
import { smallPercentageFormatter, percentageFormatter, oneDecimalPercentageFormatter } from "helpers/formatters/numbers"
import FactorTrendsRiskDetails from "modules/factor-trends-risk-details"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import Tooltip from "components/analyze-tooltip"
import MultiLine from "components/charts/multi-line"
import ExcelDownload from "components/excel-download"

export default function FactorTrendsRisk({
  displayAddView,
  availableSelectViewFactors,
  addSelectedView,
  removeSelectedView,
  selectedViews,
  startDate,
  endDate,
  firstDate,
  earliestDate,
  latestDate,
  selectedDate,
  setDate,
  resetDate,
  data,
  drift,
  items,
  options,
  selectedOption,
  only,
  toggle,
  toggleOnly,
  focusChartLine,
  focusedChartLine,
  blurChartLine,
  factorCategory,
  setFactorCategory,
  openDialog,
  fileName,
  trackCSVDownload,
}) {
  useEffect(() => {
    const adjustedStartDate = firstDate || startDate

    if (selectedDate && (selectedDate < adjustedStartDate)) {
      resetDate()
    }
  }, [firstDate, startDate, selectedDate, resetDate])

  const handleOnTabChanged = (_, id) => setFactorCategory(id)

  const handleAddView = (factor) => {
    addSelectedView(factor.id)
    setFactorCategory(factor.id)
  }

  const handleRemoveView = (factorId) => {
    if (factorCategory === factorId) {
      const optionIndex = findIndex(options, option => option.id === factorId)
      setFactorCategory(get(options, `${optionIndex - 1}.id`))
    }
    removeSelectedView(factorId)
  }

  const handleToggleOnly = (factorId) => {
    toggleOnly(factorId, only !== factorId, items)
  }

  let content = <NoData title="Predictive Risk Trends" className="graph" />

  if (earliestDate && latestDate && size(data)) {
    content = <CardContent className="card-content">
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
            displayAddView,
            openDialog,
            addView: handleAddView,
            value: factorCategory,
            factors: availableSelectViewFactors,
            onTabChanged: handleOnTabChanged,
            tabs: map(options, ({ id, label, value, isSelectedView }) => ({
              key: id,
              value: id,
              removable: isSelectedView,
              onRemoveTab: handleRemoveView,
              label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
            })),
            actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
          }}
          />
          <MultiLine
            tooltip={<Tooltip data={data} items={items} formatter={smallPercentageFormatter} />}
            {...{
              tickFormat: oneDecimalPercentageFormatter,
              date: selectedDate,
              startDate,
              endDate,
              setDate,
              data,
              onlyItem: only,
              items,
              toggle,
              toggleOnly: handleToggleOnly,
              focusChartLine,
              focusedChartLine,
              blurChartLine,
            }}
          />
        </div>
        <FactorTrendsRiskDetails
          startDate={startDate}
          endDate={endDate}
          selectedDate={selectedDate}
          selectedOption={selectedOption}
          selectedViews={selectedViews}
          details={data}
          drift={drift}
        />
      </Paper>
    </CardContent>
  }

  return <div className="modules_factor-trends-risk">
    {content}
  </div>
}

FactorTrendsRisk.propTypes = {
  account: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  displayAddView: PropTypes.bool.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  addSelectedView: PropTypes.func.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  selectedViews: PropTypes.array,
  startDate: DATE,
  endDate: DATE,
  earliestDate: DATE,
  firstDate: DATE,
  latestDate: DATE,
  selectedDate: DATE,
  setDate: PropTypes.func.isRequired,
  resetDate: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  drift: PropTypes.object.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  selectedOption: PropTypes.object.isRequired,
  only: PropTypes.string,
  toggle: PropTypes.func.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  blurChartLine: PropTypes.func.isRequired,
  factorCategory: PropTypes.string.isRequired,
  setFactorCategory: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  fileName: PropTypes.string.isRequired,
  trackCSVDownload: PropTypes.func.isRequired,
}
